// src/routes/appRoutes.js
// Single source of truth for app paths + safe returnTo handling.

const CREATORS_CRM_PATH = "/creators";
const ATTRIBUTION_OFFERS_PATH = "/attribution/offers";
const LEAD_SUCCESS_PATH = "/lead-success";

const BLOCKED_RETURN_TO_PATHS = new Set([
  "/",
  "/login",
  "/register",
  "/forgot-password",
  "/pricing",
  "/stripe/checkout/success",
  "/stripe/checkout/cancel",
  LEAD_SUCCESS_PATH,
]);

const APP_DEFAULT_RETURN_TO = "/discover";

export const safeDecode = (value) => {
  try {
    return decodeURIComponent(String(value ?? ""));
  } catch {
    return String(value ?? "");
  }
};

export const safeDecodeDeep = (value, max = 3) => {
  let current = String(value ?? "");

  for (let i = 0; i < max; i += 1) {
    const next = safeDecode(current);
    if (next === current) break;
    current = next;
  }

  return current;
};

const isAbsUrl = (value) => /^https?:\/\//i.test(String(value ?? ""));
const isProtoRelative = (value) => String(value ?? "").trim().startsWith("//");
const hasControlChars = (value) =>
  /[\u0000-\u001F\u007F]/.test(String(value ?? ""));
const hasBackslash = (value) => String(value ?? "").includes("\\");
const norm = (value) => (value == null ? "" : String(value).trim());

const getCurrentOrigin = () => {
  if (typeof window === "undefined") return "";
  return window.location?.origin || "";
};

function absoluteUrlToInternalPath(value) {
  try {
    const url = new URL(String(value || ""));

    const currentOrigin = getCurrentOrigin();

    // In browser, only accept same-origin absolute URLs.
    // In SSR/tests, reject absolute URLs to avoid open redirect surprises.
    if (!currentOrigin || url.origin !== currentOrigin) {
      return "";
    }

    return `${url.pathname}${url.search}${url.hash || ""}`.trim();
  } catch {
    return "";
  }
}

function getPathnameOnly(pathLike) {
  try {
    const url = isAbsUrl(pathLike)
      ? new URL(pathLike)
      : new URL(String(pathLike || ""), "http://local");

    return url.pathname || "";
  } catch {
    return String(pathLike || "").split("?", 1)[0].split("#", 1)[0];
  }
}

function isBlockedReturnToPath(pathLike) {
  const pathname = getPathnameOnly(pathLike);
  return BLOCKED_RETURN_TO_PATHS.has(pathname);
}

function stripNestedParams(pathLike) {
  const raw = String(pathLike ?? "").trim();
  if (!raw) return "";

  try {
    const url = isAbsUrl(raw)
      ? new URL(raw)
      : new URL(raw, "http://local");

    url.searchParams.delete("returnTo");
    url.searchParams.delete("from");
    url.searchParams.delete("next");

    return `${url.pathname}${url.search}${url.hash || ""}`.trim();
  } catch {
    try {
      const hashIndex = raw.indexOf("#");
      const beforeHash = hashIndex >= 0 ? raw.slice(0, hashIndex) : raw;
      const hash = hashIndex >= 0 ? raw.slice(hashIndex) : "";

      const queryIndex = beforeHash.indexOf("?");
      const pathname =
        queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
      const queryString =
        queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : "";

      const params = new URLSearchParams(queryString);
      params.delete("returnTo");
      params.delete("from");
      params.delete("next");

      const qs = params.toString();
      return `${pathname}${qs ? `?${qs}` : ""}${hash}`.trim();
    } catch {
      return "";
    }
  }
}

export function normalizeReturnTo(raw, fallback = "") {
  let value = String(raw ?? "").trim();
  if (!value) return fallback;

  value = safeDecodeDeep(value).trim();
  if (!value) return fallback;

  if (isProtoRelative(value)) return fallback;
  if (hasControlChars(value)) return fallback;
  if (hasBackslash(value)) return fallback;

  if (isAbsUrl(value)) {
    value = absoluteUrlToInternalPath(value);
    if (!value) return fallback;
  }

  if (!value.startsWith("/")) return fallback;
  if (value.startsWith("//")) return fallback;

  const cleaned = stripNestedParams(value);
  if (!cleaned.startsWith("/")) return fallback;
  if (cleaned.startsWith("//")) return fallback;

  if (isBlockedReturnToPath(cleaned)) {
    return fallback || APP_DEFAULT_RETURN_TO;
  }

  return cleaned;
}

function normalizeQueryParamValue(key, value) {
  if (value === undefined || value === null) return "";

  const str = String(value).trim();
  if (!str) return "";

  if (key === "returnTo" || key === "from" || key === "next") {
    return normalizeReturnTo(str);
  }

  return str;
}

export function cleanQueryParams(params = {}) {
  const out = {};

  Object.entries(params || {}).forEach(([key, value]) => {
    const normalized = normalizeQueryParamValue(key, value);
    if (!normalized) return;
    out[key] = normalized;
  });

  return out;
}

/**
 * Safely append query params to a path.
 * Works whether `path` already contains a query string or not.
 */
export function withQuery(path, paramsObj) {
  const rawPath = String(path ?? "").trim();
  if (!rawPath) return "";

  const cleanParams = cleanQueryParams(paramsObj);
  const entries = Object.entries(cleanParams);
  if (!entries.length) return rawPath;

  const hashIndex = rawPath.indexOf("#");
  const beforeHash = hashIndex >= 0 ? rawPath.slice(0, hashIndex) : rawPath;
  const hash = hashIndex >= 0 ? rawPath.slice(hashIndex) : "";

  const queryIndex = beforeHash.indexOf("?");
  const pathname =
    queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const existingQuery =
    queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : "";

  const searchParams = new URLSearchParams(existingQuery);

  entries.forEach(([key, value]) => {
    searchParams.set(key, value);
  });

  const qs = searchParams.toString();
  return `${pathname}${qs ? `?${qs}` : ""}${hash}`;
}

/**
 * Alias for semantic clarity when the caller is intentionally
 * merging params into an already query-aware route.
 */
export function mergeQuery(path, paramsObj) {
  return withQuery(path, paramsObj);
}

export const enc = (value) => encodeURIComponent(String(value ?? "").trim());

export function pickParam(source, key) {
  if (!source || !key) return "";

  try {
    if (typeof source.get === "function") {
      return norm(source.get(key));
    }
  } catch {
    // ignore
  }

  return norm(source[key]);
}

export const ROUTES = {
  // Public
  homePublic: "/",
  pricing: "/pricing",
  faq: "/faq",
  contact: "/contact",
  privacy: "/privacy",
  terms: "/terms",
  cookies: "/cookies",
  security: "/security",
  support: "/contact-support",
  login: "/login",
  register: "/register",
  forgotPassword: "/forgot-password",
  error: "/error",
  leadSuccess: LEAD_SUCCESS_PATH,

  // Public marketing / SEO pages
  useCases: "/use-cases",
  locations: "/locations",
  insights: "/insights",
  insight: (slug) => `/insights/${enc(slug)}`,

  useCaseRestaurants: "/use-cases/restaurants",
  useCaseCafes: "/use-cases/cafes",
  useCaseBeautySalons: "/use-cases/beauty-salons",
  useCaseHairBeauty: "/use-cases/hair-beauty",
  useCaseNailsLashes: "/use-cases/nails-lashes",
  useCaseGyms: "/use-cases/gyms",
  useCaseLocalLifestyle: "/use-cases/local-lifestyle",
  useCaseBusinessesWithoutWebsite: "/use-cases/businesses-without-website",
  locationLondon: "/locations/london",
  locationManchester: "/locations/manchester",
  locationBirmingham: "/locations/birmingham",
  useCase: (slug) => `/use-cases/${enc(slug)}`,
  location: (slug) => `/locations/${enc(slug)}`,

  // Core app
  home: "/discover",
  discover: "/discover",
  search: "/influencer-search",
  compare: "/compare",
  savedCreators: "/saved",

  // Hidden preview route - not added to nav/sidebar yet
  creatorAdvisorPreview: "/creator-advisor-preview",

  // Admin
  adminCandidateImport: "/admin/candidates/import",
  adminCreatorRequests: "/admin/creator-requests",
  adminBusinessProspects: "/admin/business-prospects",

  // Creators CRM
  creators: CREATORS_CRM_PATH,

  creatorsCrm: (params = {}) =>
    withQuery(CREATORS_CRM_PATH, cleanQueryParams(params)),

  campaignCreatorsCrm: (campaignId, params = {}) =>
    withQuery(CREATORS_CRM_PATH, cleanQueryParams({ campaignId, ...params })),

  creatorCrm: (creatorKey, params = {}) =>
    withQuery(
      CREATORS_CRM_PATH,
      cleanQueryParams({
        ...(norm(creatorKey) ? { creator: creatorKey } : {}),
        ...params,
      })
    ),

  // Profile / account
  completeProfile: "/complete-profile",
  accountSettings: "/account-settings",
  profileSettings: "/profile-settings",
  accountLayout: "/account-layout",

  // Subscription
  subscriptionManagement: "/subscription-management",
  subscriptionPlans: "/subscription-plans",
  subscriptionSelection: "/subscription-selection",

  upgradePlans: (params = {}) =>
    withQuery("/subscription-plans", cleanQueryParams(params)),

  // Stripe redirects
  stripeSuccess: "/stripe/checkout/success",
  stripeCancel: "/stripe/checkout/cancel",

  // Campaigns
  campaigns: "/campaigns",
  campaignNew: "/campaigns/new",
  campaignDetails: (id) => `/campaigns/${enc(id)}`,
  campaignEdit: (id) => `/campaigns/${enc(id)}/edit`,
  campaignRoiAnalytics: (id) => `/campaigns/${enc(id)}/roi-analytics`,

  // Attribution / offers
  attributionOffers: ATTRIBUTION_OFFERS_PATH,

  attributionOfferNew: (params = {}) =>
    withQuery(`${ATTRIBUTION_OFFERS_PATH}/new`, cleanQueryParams(params)),

  attributionOffer: (offerId, params = {}) =>
    withQuery(
      `${ATTRIBUTION_OFFERS_PATH}/${enc(offerId)}`,
      cleanQueryParams(params)
    ),

  attributionOfferInvites: (offerId, params = {}) =>
    withQuery(
      `${ATTRIBUTION_OFFERS_PATH}/${enc(offerId)}/invites`,
      cleanQueryParams(params)
    ),

  attributionOfferRedemptions: (offerId, params = {}) =>
    withQuery(
      `${ATTRIBUTION_OFFERS_PATH}/${enc(offerId)}/redemptions`,
      cleanQueryParams(params)
    ),

  attributionOfferRoi: (offerId, params = {}) =>
    withQuery(
      `${ATTRIBUTION_OFFERS_PATH}/${enc(offerId)}/roi`,
      cleanQueryParams(params)
    ),

  // Influencers
  influencerDetails: (influencerId) => `/influencer/${enc(influencerId)}`,

  // Public invite / onboarding
  creatorOnboard: "/creator/onboard",
  creatorOnboardDetails: "/creator/onboard/details",
  publicInvite: (inviteId) => `/i/${enc(inviteId)}`,
  // Public customer promotion
  publicPromotion: (pageSlug) => `/p/${enc(pageSlug)}`,
  // Legacy alias
  influencerCompareLegacy: "/influencer-compare",
};

export function resolveReturnTo({ searchParams, state, campaignId } = {}) {
  const candidates = [
    pickParam(searchParams, "returnTo"),
    pickParam(searchParams, "from"),
    norm(state?.returnTo),
    norm(state?.from),
    pickParam(searchParams, "next"),
    norm(state?.next),
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;

    const normalized = normalizeReturnTo(candidate);
    if (normalized) return normalized;
  }

  return campaignId ? ROUTES.campaignDetails(campaignId) : ROUTES.campaigns;
}

export function resolvePostCheckoutReturnTo(raw) {
  return normalizeReturnTo(raw, APP_DEFAULT_RETURN_TO) || APP_DEFAULT_RETURN_TO;
}